import { tooltip } from "./control-bar.js";
import { CameraIcon, CameraOffIcon, MicIcon, MicOffIcon } from "./icons.js";

/**
 * The two device switches, as one button drawn in two places (flow ticket 04).
 *
 * They were the control bar's until this ticket, and the bar was mounted on both
 * screens. It is the prompter's alone now — but the switches are about the
 * *preview*, so in the green room they went onto it, and a module of their own is
 * what stops the room's overlay and the prompter's bar drifting into two buttons
 * that agree today.
 *
 * **Nothing about how the state is decided came with them.** `roomToggles` in
 * `green-room.ts` still decides which circles are live and which way they are set,
 * `WebApp` still builds both {@link DeviceToggle}s once and hands the same two
 * objects to whoever draws them, and there is still no `useState` behind either
 * button: the press goes to the worker, the worker says what is off, and the
 * preview and the take read the same answer back. That rule is what lets a take
 * borrow the green room's already-open camera instead of closing and reopening the
 * hardware at "action" (ADR-0024) — and a refactor is exactly the kind of change
 * that breaks it quietly.
 */

/**
 * A device the page can turn off, where there is one.
 *
 * **Optional, and it stays optional now that both are wired.** A surface with no
 * device passes nothing and the button renders dead — which is not a placeholder
 * but exactly the state the brief draws for a camera on a machine that has none.
 * Nothing here invents a reason for the tooltip to give, because "there is no
 * device" is not a sentence a user can act on.
 *
 * Which of the two circles is in which case is `roomToggles` in `green-room.ts`,
 * decided once by the host and handed to both surfaces.
 */
export interface DeviceToggle {
  /** Whether the device is live. `false` inverts the button to the active-off
   * state: a `--record-deep` fill under a slashed glyph. */
  on: boolean;
  /**
   * Whether the switch is refused for now, with the state it is in still drawn.
   *
   * The difference between this and handing over no device at all is the whole
   * reason it exists: a take in progress cannot have its devices changed — the
   * footage is already being written — but a microphone switched off *before* the
   * take is still off, and a button that went back to its plain glyph for the
   * length of the take would say the opposite of what is true at the one moment
   * it matters most.
   */
  disabled?: boolean;
  /** Why it is refused, for the `title`. */
  reason?: string;
  onToggle: () => void;
}

/** Which of the two. */
export type CircleKind = "microphone" | "camera";

/**
 * The pair, as one thing to hand about.
 *
 * They travel together everywhere — decided together by `roomToggles`, built
 * together by the host, drawn together on both surfaces — because the one state
 * where they disagree is the reason they are decided together at all: a machine
 * with no camera has a microphone and no camera switch.
 *
 * Both optional, for the reason {@link DeviceToggle} is: a surface with no device
 * passes nothing and the button renders dead.
 */
export interface DeviceCircles {
  camera?: DeviceToggle;
  microphone?: DeviceToggle;
}

/**
 * What each kind is called and what it is drawn with.
 *
 * The label and the two glyphs travel with the kind rather than with the call
 * site, so a microphone button labelled "Camera" is not a thing a second surface
 * can produce — which is the failure this module exists to make impossible, and
 * one that looks like nothing at all on screen because these buttons have no
 * words on them.
 */
const DEVICES: Record<
  CircleKind,
  { label: string; on: JSX.Element; off: JSX.Element }
> = {
  microphone: {
    label: "Microphone",
    on: <MicIcon />,
    off: <MicOffIcon />,
  },
  camera: {
    label: "Camera",
    on: <CameraIcon />,
    off: <CameraOffIcon />,
  },
};

/**
 * One of the two device toggles.
 *
 * Dead when the host has handed it no device, and dead with its state still drawn
 * when the host has refused the press — see {@link DeviceToggle}.
 *
 * The tooltip is the label alone unless there is a refusal to name, and never a
 * key: neither of these is in the shortcut table, and naming one they do not have
 * would be worse than saying nothing.
 */
export function DeviceCircle({
  kind,
  className,
  device,
}: {
  kind: CircleKind;
  /**
   * The surface's own handle — `web-bar-mic` on the prompter's bar,
   * `web-stage-mic` over the green room's preview.
   *
   * Written out at each call site rather than built from a prefix, because these
   * are the names the browser drivers press by and a concatenated class name is
   * one that nothing can grep for.
   */
  className: string;
  device?: DeviceToggle;
}): JSX.Element {
  const { label, on: onIcon, off: offIcon } = DEVICES[kind];
  // A device the host has not handed over is not "off" — there is nothing to
  // turn on. So the *inverted* state is the one case where a device exists and
  // says so, and everything else falls through to a dead button with its plain
  // glyph.
  const off = device !== undefined && !device.on;
  return (
    <button
      type="button"
      // `web-circle` is the shared look, carried on both surfaces so the two
      // screens draw one button rather than two that happen to match.
      className={`web-circle ${className}${off ? " is-off" : ""}`}
      aria-label={label}
      aria-pressed={device === undefined ? undefined : device.on}
      // The label survives the reason rather than being replaced by it: "Not
      // while a take is running" over an unlabelled circle is an explanation of
      // something the reader has not been told the name of.
      title={
        device?.reason === undefined
          ? tooltip(label)
          : `${label} — ${device.reason}`
      }
      onClick={() => device?.onToggle()}
      disabled={device === undefined || device.disabled === true}
    >
      {off ? offIcon : onIcon}
    </button>
  );
}
